import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogOpenEvent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];
  #controller = new AbortController();
  #request = 0;
  #initialMarkup = '';
  #recentMarkup = '';
  #index = -1;

  connectedCallback() {
    super.connectedCallback();
    this.#initialMarkup = this.refs.predictiveSearchResults.innerHTML;
    const { signal } = this.#controller;
    this.closest('dialog-component')?.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal });
    document.addEventListener('keydown', this.#onShortcut, { signal });
    this.refs.searchInput.addEventListener('compositionend', this.search, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#request++;
  }

  #onShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.closest('dialog-component')?.toggleDialog();
    }
  };

  #onOpen = () => {
    this.refs.searchInput.focus();
    if (!this.refs.searchInput.value.trim()) this.#loadRecent();
  };

  #status(message = '') {
    const status = this.querySelector('[data-search-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  }

  #clearSelection() {
    this.#index = -1;
    this.refs.searchInput.removeAttribute('aria-activedescendant');
    this.querySelectorAll('[data-search-option]').forEach((item) => item.setAttribute('aria-selected', 'false'));
  }

  search = (event) => {
    this.#request++;
    this.#clearSelection();
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    if (!event.isComposing) this.#debouncedSearch();
  };

  #debouncedSearch = debounce(() => {
    const term = this.refs.searchInput.value.trim();
    if (!term) {
      this.resetSearch(false);
      return;
    }
    this.#getSearchResults(term);
  }, 180);

  async #getSearchResults(term) {
    if (!this.dataset.sectionId) return;
    const request = ++this.#request;
    this.#clearSelection();
    this.refs.resetButton.hidden = false;
    this.dataset.searchState = 'loading';
    this.refs.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.#status(this.dataset.loadingLabel);
    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    try {
      const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url);
      if (request !== this.#request || this.refs.searchInput.value.trim() !== term) return;
      const response = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-search-results]');
      if (!response) throw new Error('Search response unavailable');
      this.refs.predictiveSearchResults.replaceChildren(response);
      this.refs.predictiveSearchResults.scrollTop = 0;
      this.dataset.searchState = 'results';
      this.refs.searchInput.setAttribute('aria-expanded', String(!!response.querySelector('[data-search-option]')));
      this.#status();
    } catch {
      if (request !== this.#request) return;
      this.dataset.searchState = 'error';
      this.refs.predictiveSearchResults.replaceChildren();
      this.#status(this.dataset.errorLabel);
    } finally {
      if (request === this.#request) this.refs.predictiveSearchResults.removeAttribute('aria-busy');
    }
  }

  resetSearch = (keepFocus = true) => {
    this.#request++;
    this.#clearSelection();
    this.refs.searchInput.value = '';
    this.refs.resetButton.hidden = true;
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.refs.predictiveSearchResults.removeAttribute('aria-busy');
    this.refs.predictiveSearchResults.innerHTML = this.#initialMarkup;
    this.refs.predictiveSearchResults.scrollTop = 0;
    this.dataset.searchState = 'empty';
    this.#status();
    this.#loadRecent();
    if (keepFocus) this.refs.searchInput.focus();
  };

  async #loadRecent() {
    const target = this.querySelector('[data-recently-viewed]');
    const ids = RecentlyViewed.getProducts();
    if (!target || !ids.length || this.refs.searchInput.value.trim()) return;
    const request = this.#request;
    try {
      if (!this.#recentMarkup) {
        const url = new URL(Theme.routes.search_url, location.origin);
        url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
        url.searchParams.set('type', 'product');
        const markup = await sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url);
        const products = new DOMParser().parseFromString(markup, 'text/html').querySelector('#predictive-search-products');
        this.#recentMarkup = products?.outerHTML || '';
      }
      if (request === this.#request && !this.refs.searchInput.value.trim() && target.isConnected) {
        target.innerHTML = this.#recentMarkup;
      }
    } catch {
      this.#recentMarkup = '';
    }
  }

  clearRecentlyViewedProducts(event) {
    event.preventDefault();
    RecentlyViewed.clearProducts();
    this.#recentMarkup = '';
    this.querySelector('[data-recently-viewed]')?.replaceChildren();
    this.refs.searchInput.focus();
  }

  onSearchKeyDown = (event) => {
    if (event.isComposing || event.target !== this.refs.searchInput) return;
    const items = Array.from(this.querySelectorAll('[data-search-option]'));
    if (event.key === 'Enter' && this.#index >= 0 && items[this.#index]) {
      event.preventDefault();
      items[this.#index].querySelector('a')?.click();
      return;
    }
    if (this.dataset.searchState !== 'results' || this.refs.searchInput.getAttribute('aria-expanded') !== 'true' || !items.length || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    this.#index = this.#index < 0
      ? (event.key === 'ArrowDown' ? 0 : items.length - 1)
      : (this.#index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.#index)));
    const selected = items[this.#index];
    this.refs.searchInput.setAttribute('aria-activedescendant', selected.id);
    selected.scrollIntoView({ block: 'nearest' });
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
